import React, { Suspense } from "react"
import { FileGallery } from "core/file-gallery"
import Label from "@kit/design/label"
import { useToggle } from "hooks/use-toggle"
import Icon from "@kit/design/icon"
import { OldButton } from "@kit/design/old-button"
import FileKit from "@convertkit/file-kit"
import { useCurrentUser } from "hooks/use-current-user"

const DokaModal = React.lazy(() => import("vendor/lazy-doka.js"))

export function ImageUploader({ name, label, image, update, remove }) {
  const [fileGalleryOpen, toggleFileGallery] = useToggle(false)
  const user = useCurrentUser()

  function close(file) {
    toggleFileGallery()
    if (file?.dataURL) update(file)
  }

  const handleEditConfirm = async output => {
    const filekit = FileKit({
      authURL: "/filekit/sessions",
      filekitURL: process.env.FILEKIT_HOST,
      id: user.account_id
    })
    const result = await filekit.upload(output.file)

    update({ dataURL: result.original_url })
  }

  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 relative">
        <button
          onClick={toggleFileGallery}
          className="block w-full"
          data-image-uploader={name}
        >
          {image ? (
            // Image Preview
            <div
              style={{ backgroundImage: `url(${image})` }}
              className="w-full h-20 bg-gray-50 bg-cover bg-center rounded flex items-center justify-center"
            />
          ) : (
            // Blank Image Preview
            <div className="w-full h-20 flex items-center justify-center border-2 border-dashed border-gray-100 rounded text-center text-gray-500 hover:border-gray-700 hover:text-gray-800 transition-colors duration-300 ease-in-out">
              <Icon name="cloud-upload" />
            </div>
          )}
        </button>

        {image && (
          <>
            {/* Doka Editor Button */}
            <ImageEditorButton src={image} onChange={handleEditConfirm} />

            {/* Remove Button */}
            <button
              onClick={remove}
              className="bg-white text-gray-900 absolute top-0 right-0 rounded-full w-6 h-6 -mt-3 -mr-3 flex items-center justify-center shadow hover:text-red-600 transition-colors ease-in-out duration-200"
            >
              <Icon name="close" />
            </button>
          </>
        )}

        <FileGallery
          active={fileGalleryOpen}
          onClose={close}
          allowedFileTypes={["image"]}
        />
      </div>
    </div>
  )
}

function ImageEditorButton({ src, onChange }) {
  const [active, toggle] = useToggle()

  const handleChange = output => {
    onChange(output)
    toggle(false)
  }

  if (!src) return null

  return (
    <>
      <OldButton
        is="secondary"
        className="mt-2 w-full"
        onClick={e => {
          e.stopPropagation()
          toggle()
        }}
      >
        <Icon name="edit" className="mr-2" />
        Edit image
      </OldButton>
      <Suspense fallback={null}>
        {active && (
          <DokaModal
            style={{ maxWidth: "200px", height: "200px" }}
            src={src}
            onConfirm={handleChange}
            onCancel={toggle}
          />
        )}
      </Suspense>
    </>
  )
}
